import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { BLOG_POSTS } from "@/lib/blog-posts";
import { buildCollectionLd } from "@/lib/page-schemas";
import { listCustomBlogPosts } from "@/lib/blog/admin.functions";
import type { RichBlogSummary } from "@/lib/blog/rich-types";

export const Route = createFileRoute("/blog/")({
  loader: async () => {
    try {
      const res = await listCustomBlogPosts();
      return { custom: res.items as RichBlogSummary[] };
    } catch {
      return { custom: [] as RichBlogSummary[] };
    }
  },
  head: () => ({
    meta: [
      { title: "Gold Price Blog — Buying Guides, Karat Tips & Market Insights" },
      {
        name: "description",
        content:
          "Practical, honest articles on buying gold in India and Pakistan: 22K vs 24K, investment tips, how prices are calculated, and timing the market.",
      },
      { property: "og:title", content: "Gold Price Blog" },
      { property: "og:description", content: "Honest guides on buying gold in India and Pakistan." },
      { property: "og:url", content: "https://drhint.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/blog" }],
    scripts: buildCollectionLd({
      url: "https://drhint.com/blog",
      name: "DrHint Gold Price Blog",
      description: "Buying guides, karat tips and market insights for gold buyers in Pakistan and India.",
      items: BLOG_POSTS.map((p) => ({ url: `https://drhint.com/blog/${p.slug}`, name: p.title, description: p.description, datePublished: p.date })),
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "Blog", item: "https://drhint.com/blog" },
      ],
    }),
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const { custom } = Route.useLoaderData() as { custom: RichBlogSummary[] };
  const legacySlugs = new Set(BLOG_POSTS.map((p) => p.slug));
  const merged = [
    ...custom
      .filter((p) => !legacySlugs.has(p.slug))
      .map((p) => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        date: p.date,
        readMinutes: p.readMinutes,
      })),
    ...BLOG_POSTS.map((p) => ({
      slug: p.slug,
      title: p.title,
      description: p.description,
      date: p.date,
      readMinutes: p.readMinutes,
    })),
  ].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl md:text-4xl font-bold">Gold Buying Guides &amp; Insights</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          Practical articles on gold buying, mobile codes, telecom tips and more — every guide is hand-written by the DrHint desk.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {merged.map((post) => (
            <Link
              key={post.slug}
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="group block rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="text-xs text-muted-foreground mb-2">
                {new Date(post.date).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" })} ·{" "}
                {post.readMinutes} min read
              </div>
              <h2 className="font-semibold text-lg leading-snug group-hover:text-primary transition-colors">
                {post.title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{post.description}</p>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}